<template>
    <!-- Previous deleted organizations -->
    <b-container fluid class="overview">
        <h1 class="text-left">
            {{ $t('functionResult.labelOverviewTitle') }}
        </h1>
        <div class="col-xs-8 col-md-5">
            <x-select
                name="functionResult"
                :text="$t('functionResult.labelFunctionResult')"
                id="LayerSelect"
                :options="functionList"
                v-model="functionResult"
                :simpleMode="true"
                :showDefaultOption="true"
                :textDefaultOption="
                    $t('functionResult.labelSelectFunctionResult')
                "
            >
            </x-select>
        </div>
        <div
            v-if="!loading && functionResult && functionResultsList.length > 0"
        >
            <b-table
                id="table-function-results"
                striped
                hover
                small
                outlined
                bordered
                :per-page="perPage"
                :current-page="currentPage"
                :sticky-header="btableMaxHeight"
                :select-mode="'single'"
                tbody-tr-class="tablerow"
                :items="functionResultsList"
                :fields="functionResultFields"
                sort-icon-left
            >
                <template v-slot:cell(content)="{ item }">
                    <!-- Button to open modal with table content-->
                    <b-button
                        size="sm"
                        variant="secondary"
                        @click="$_showContent(item.id)"
                    >
                        {{ $t('functionResult.showDetails') }}
                    </b-button>
                </template>
            </b-table>
            <b-pagination
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
                aria-controls="table-function-results"
            ></b-pagination>
        </div>
        <div
            v-if="!loading && functionResult && functionResultsList.length == 0"
        >
            {{ $t('functionResult.noData') }}
        </div>
        <function-result-detail
            :launchModal="launchDetailContentModal"
            :logRecordId="logRecordId"
            :functionResult="getFunctionResult"
        />
    </b-container>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { nprogress, functionResultName } from '../../services/utilities';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import FunctionResultDetails from './FunctionResultDetails.vue';
    import { FUNCTION_RESULTS } from '../../constants';
    import XSelect from '../../components/BaseComponents/x-select.vue';

    export default {
        name: 'FunctionResultsOverview',

        mixins: [modalMixin],

        components: {
            'function-result-detail': FunctionResultDetails,
            'x-select': XSelect
        },

        data() {
            return {
                launchDetailContentModal: false,
                logRecordId: 0,
                functionResult: '',
                loading: true,
                perPage: 15,
                currentPage: 1,
                functionResultsList: [],
                functionResultFields: [
                    {
                        key: 'startDateTime',
                        label: this.$t('functionResult.startDateTime'),
                        sortable: true,
                        thStyle: { border: 0, width: '200px' },
                        formatter: (value) => {
                            return new Date(value).toLocaleString(
                                this.$store.state.userInfo.locale
                            );
                        }
                    },
                    {
                        key: 'endDateTime',
                        label: this.$t('functionResult.endDateTime'),
                        sortable: false,
                        thStyle: { border: 0, width: '200px' },
                        formatter: (value) => {
                            return new Date(value).toLocaleString(
                                this.$store.state.userInfo.locale
                            );
                        }
                    },
                    {
                        key: 'organizationName',
                        label: this.$t('functionResult.organizationName'),
                        sortable: false,
                        thStyle: { border: 0, width: '100x' }
                    },
                    {
                        key: 'userName',
                        label: this.$t('functionResult.userName'),
                        sortable: false,
                        thStyle: { border: 0, width: '100px' }
                    },
                    {
                        key: 'statusDescription',
                        label: this.$t('functionResult.statusDescription'),
                        sortable: false,
                        thStyle: { border: 0, width: '100px' }
                    },
                    {
                        key: 'status',
                        label: this.$t('functionResult.status'),
                        sortable: false,
                        thStyle: { border: 0, width: '100px' }
                    },
                    {
                        key: 'content',
                        label: '',
                        thStyle: { border: 0, width: '100px' }
                    }
                ],
                btableMaxHeight: `${(window.innerHeight - 300).toString()}px`,
                functionList: []
            };
        },

        computed: {
            ...mapGetters(['getUserInfo', 'getLocale']),

            userInfo() {
                return this.getUserInfo;
            },

            rows() {
                return this.functionResultsList.length;
            },

            getFunctionResult() {
                return this.functionResult ?? '';
            }
        },
        async created() {
            nprogress.start();
            this.loading = true;
            // Get all the data from the backend
            await this.$_getFunctionList();
            this.loading = false;
            nprogress.done();
        },
        mounted() {
            // to update b-table max-height to have a freeze header (sticky-header makes fixed max-height only regardless the screen height)
            // placed a new issue in Git, see if we get any response.
            this.$nextTick(() => {
                window.addEventListener('resize', () => {
                    // debugger
                    this.btableMaxHeight = `${(
                        window.innerHeight - 300
                    ).toString()}px`; // where offset is some kind of constant margin you need from the top
                });
            });
        },
        beforeDestroy() {
            window.removeEventListener('resize', () => {});
        },

        methods: {
            async $_getfunctionResultsList(functionName) {
                nprogress.start();
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFunctionResultsOverview',
                    parameters: { Function: functionName }
                });
                this.functionResultsList = result.data ?? [];

                nprogress.done();
            },
            $_showContent(id) {
                this.logRecordId = id;
                this.launchDetailContentModal = !this.launchDetailContentModal;
            },
            $_getFunctionList() {
                let list = [];
                for (const [, value] of Object.entries(FUNCTION_RESULTS)) {
                    list.push({ value: value, text: functionResultName(value) });
                }
                this.functionList = list;
            }
        },
        watch: {
            functionResult: async function (newValue) {
                this.functionResultsList = [];
                await this.$_getfunctionResultsList(newValue);
            }
        }
    };
</script>

<style lang="scss" scoped>
.tableFixHead {
    overflow-y: auto; /* make the table scrollable if height is more than 200 px  */
    height: 90%; /* gives an initial height of 200px to the table */
}

.tableFixHead thead-class th {
    position: sticky; /* make the table heads sticky */
    top: 0px; /* table head will be placed from the top of the table and sticks to it */
}

table {
    border-collapse: collapse; /* make the table borders collapse to each other */
    width: 100%;
}

thead-class,
td {
    padding: 8px 16px;
    border: 1px solid #ccc;
    position: sticky;
}

thead-class {
    background: #eee;
}
</style>
